import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Spin,
  Switch,
  Table,
  Tag,
  message,
} from 'antd';
import { adminFetch, AdminUnauthorized, getAdminToken } from './admin-api';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Any = any;

const fmt = (v: Any) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

const DEFAULTS = {
  name: '',
  display_name: '',
  description: '',
  cost: 100000,
  item_order: 1,
  visible: true,
};

export const AdminOptionsView = () => {
  const [authed, setAuthed] = useState(!!getAdminToken());
  const [pwd, setPwd] = useState('');
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<Any[]>([]);
  const [categories, setCategories] = useState<Any[]>([]);
  const [form] = Form.useForm();
  const [editing, setEditing] = useState<Any | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async (token?: string) => {
    setLoading(true);
    try {
      const [o, c] = await Promise.all([
        adminFetch('/admin/configuration_items', { token }),
        adminFetch('/admin/configuration_items/categories', { token }),
      ]);
      setOptions(o || []);
      setCategories(c || []);
      setAuthed(true);
      if (token) localStorage.setItem('admin_token', token);
    } catch (e) {
      if (e instanceof AdminUnauthorized) {
        message.error('Неверный пароль администратора');
        localStorage.removeItem('admin_token');
        setAuthed(false);
      } else {
        message.error('Не удалось загрузить опции');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (getAdminToken()) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const open = (record?: Any) => {
    setEditing(record || {});
    form.setFieldsValue(
      record
        ? record
        : { ...DEFAULTS, category_id: categories[0]?.id }
    );
  };

  const save = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const path = editing?.id
        ? `/admin/configuration_items/${editing.id}`
        : '/admin/configuration_items';
      await adminFetch(path, {
        method: editing?.id ? 'PATCH' : 'POST',
        body: { configuration_item: values },
      });
      message.success('Опция сохранена');
      setEditing(null);
      await load();
    } catch (e) {
      message.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    try {
      await adminFetch(`/admin/configuration_items/${id}`, { method: 'DELETE' });
      message.success('Опция удалена');
      await load();
    } catch (e) {
      message.error((e as Error).message);
    }
  };

  if (!authed) {
    return (
      <div className="container max-w-[420px] py-16">
        <h2 className="text-[20px] font-semibold mb-4">Справочник опций</h2>
        <Input.Password
          placeholder="Пароль администратора"
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          onPressEnter={() => load(pwd)}
          className="mb-3"
        />
        <Button type="primary" block loading={loading} onClick={() => load(pwd)}>
          Войти
        </Button>
      </div>
    );
  }

  const columns = [
    { title: 'Опция', dataIndex: 'display_name' },
    {
      title: 'Системное имя',
      dataIndex: 'name',
      render: (v: string) => <span className="text-[#888]">{v}</span>,
    },
    {
      title: 'Доплата, ₽',
      dataIndex: 'cost',
      render: (v: number) => fmt(v),
    },
    {
      title: 'Видимость',
      dataIndex: 'visible',
      render: (v: boolean) =>
        v ? <Tag color="green">видна</Tag> : <Tag>скрыта</Tag>,
    },
    {
      title: '',
      key: 'actions',
      width: 170,
      render: (_: Any, o: Any) => (
        <div className="flex gap-2">
          <Button size="small" onClick={() => open(o)}>
            Изменить
          </Button>
          <Popconfirm
            title="Удалить опцию?"
            description="Удалит правила совместимости по этой опции во всех модификациях."
            okText="Удалить"
            cancelText="Отмена"
            onConfirm={() => remove(o.id)}>
            <Button size="small" danger>
              Удалить
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="container pb-[120px]">
      <div className="flex items-center justify-between my-6">
        <h2 className="text-[22px] font-semibold">Справочник опций</h2>
        <Link to="/admin/catalog" className="text-[#007E3C] underline">
          ← Модели и модификации
        </Link>
      </div>

      <Button
        type="primary"
        className="mb-4"
        style={{ background: '#66B28A' }}
        onClick={() => open()}>
        + Добавить опцию
      </Button>

      <Spin spinning={loading}>
        <Table
          rowKey="id"
          pagination={false}
          columns={columns}
          dataSource={options}
        />
      </Spin>

      <Modal
        title={editing?.id ? 'Изменить опцию' : 'Новая опция'}
        open={!!editing}
        onCancel={() => setEditing(null)}
        onOk={save}
        confirmLoading={saving}
        okText="Сохранить"
        cancelText="Отмена"
        width={620}>
        <Form form={form} layout="vertical">
          <Form.Item
            name="display_name"
            label="Название (для клиента)"
            rules={[{ required: true, message: 'Обязательно' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="name"
            label="Системное имя (латиницей, уникальное)"
            rules={[{ required: true, message: 'Обязательно' }]}>
            <Input placeholder="hydraulic_slide_gates" />
          </Form.Item>
          <Form.Item name="category_id" label="Категория" rules={[{ required: true }]}>
            <Select
              options={categories.map((c) => ({ value: c.id, label: c.name }))}
            />
          </Form.Item>
          <Form.Item
            name="cost"
            label="Доплата, ₽"
            rules={[{ required: true, message: 'Обязательно' }]}>
            <InputNumber min={1} step={1000} formatter={fmt} className="w-full" />
          </Form.Item>
          <Form.Item name="item_order" label="Порядок" rules={[{ required: true }]}>
            <InputNumber min={1} className="w-full" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Описание"
            rules={[{ required: true, message: 'Обязательно' }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="visible" label="Видна клиенту" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
